import { useRef, useState } from "react";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { MapPin } from "lucide-react";
import { cn } from "../lib/utils";

const STATS = [
  { label: "Erfahrung", value: "5+ Jahre" },
  { label: "Nutzer betreut", value: "1000+" },
  { label: "Support auf", value: "DE · EN" },
  { label: "Zertifiziert", value: "M365" },
];

/**
 * Interactive flip card for the profile photo — click/tap to flip between the
 * front (photo + key stats) and back (short personal note + mini photo).
 * Also tilts gently based on scroll position. Lightweight CSS 3D transform,
 * no WebGL — pairs with the heavier lanyard card in the hero.
 */
const ProfileFlipCard = ({ className }) => {
  const containerRef = useRef(null);
  const [isFlipped, setIsFlipped] = useState(false);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });

  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 20, mass: 0.5 });
  const rotate = useTransform(progress, [0, 0.5, 1], [-4, 0, 4]);

  return (
    <div ref={containerRef} className={cn("flip-perspective", className)}>
      {/* Outer wrapper: scroll-based 2D tilt only */}
      <motion.div style={{ rotate }}>
        {/* Inner wrapper: click-driven 3D flip, animated separately so the two
            transforms don't collide (inline style always wins over CSS classes) */}
        <motion.div
          data-testid="profile-flip-card"
          role="button"
          tabIndex={0}
          aria-pressed={isFlipped}
          onClick={() => setIsFlipped((f) => !f)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setIsFlipped((f) => !f);
            }
          }}
          animate={{ rotateY: isFlipped ? 180 : 0 }}
          transition={{ duration: 0.7, ease: [0.4, 0.2, 0.2, 1] }}
          style={{ transformStyle: "preserve-3d" }}
          className="relative cursor-pointer"
        >
          {/* Front face */}
          <div className="flip-face rounded-2xl border bg-card p-6 shadow-sm space-y-5">
            <div className="flex flex-col items-center gap-3 text-center">
              <img
                src="/images/hero.webp"
                alt="Andreas Fischinger"
                className="size-24 rounded-full object-cover border-2 border-accent/30"
              />
              <div>
                <p className="font-semibold text-foreground text-lg">Andreas Fischinger</p>
                <p className="text-sm text-muted-foreground flex items-center justify-center gap-1">
                  <MapPin className="size-3.5 text-accent" strokeWidth={1.8} />
                  Zürich, Schweiz
                </p>
              </div>
            </div>

            <div className="divide-y divide-border border-t">
              {STATS.map((stat) => (
                <div key={stat.label} className="flex items-center justify-between py-2.5">
                  <span className="text-sm text-muted-foreground">{stat.label}</span>
                  <span className="text-base font-semibold text-accent">{stat.value}</span>
                </div>
              ))}
            </div>

            <p className="text-xs text-center text-muted-foreground/70">Zum Umdrehen klicken oder tippen</p>
          </div>

          {/* Back face */}
          <div className="flip-face flip-face-back rounded-2xl border bg-foreground text-background p-6 flex flex-col justify-between gap-5 shadow-sm">
            <div>
              <p className="text-xs text-background/60">Steve Jobs</p>
              <p className="text-sm font-medium mt-1 leading-relaxed">
                &ldquo;You've got to start with the customer experience and work backwards to the technology, not the other way around.&rdquo;
              </p>
            </div>
            <div className="flex items-end justify-between gap-3">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-400">
                <span className="relative inline-flex size-1.5">
                  <span className="animate-ping-slow absolute inset-0 rounded-full bg-green-400/50" />
                  <span className="relative inline-flex size-1.5 rounded-full bg-green-400" />
                </span>
                Verfügbar ab Oktober 2026
              </span>
              <img
                src="/images/hero.webp"
                alt="Andreas Fischinger"
                className="size-14 rounded-lg object-cover shrink-0"
              />
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ProfileFlipCard;
